import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import type { Constant } from '@pbt/pbt-ui-components'
import {
  ClientAndPatient as ClientAndPatientIcon,
  Communications as CommunicationsIcon,
  Estimates as EstimatesIcon,
  Print as PrintIcon,
  Sms as SmsIcon,
  Whiteboard as WhiteboardIcon,
} from '@pbt/pbt-ui-components/src/icons'

import type { PopperAction } from '~/components/common/ActionsPopper'
import { useGetIsNoShowConsentAppointmentType } from '~/components/common/appointments/useGetIsNoShowConsentAppointmentType'
import useConfirmAppointmentNoShowAlert from '~/components/common/dialog/useConfirmAppointmentNoShowAlert'
import { AppointmentState } from '~/constants/appointmentStates'
import { isHighValueAppointment } from '~/constants/appointmentTypes'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import {
  LandingType,
  LandingWidgetName,
  WidgetWidthType,
} from '~/constants/landingConstants'
import { BaseRoute } from '~/constants/routes'
import i18n from '~/locales/i18n'
import { createSOAP } from '~/store/actions/soap'
import { patchAppointment } from '~/store/actions/timetable'
import {
  fetchWidgetsData,
  getWidgetData,
  getWidgetDataIsLoading,
  getWidgetDataTotalCount,
} from '~/store/duck/landing'
import { useOpenInvoice } from '~/store/hooks/finance'
import {
  getCurrentBusinessIsAppointmentCancellationReasonEnabled,
  getCurrentBusinessIsOmniChannel,
} from '~/store/reducers/auth'
import {
  getAppointmentsWidgetAssignedMode,
  getFeatureToggle,
} from '~/store/reducers/constants'
import { getSOAPisLoading } from '~/store/reducers/soap'
import {
  getMultipleTimetableEvents,
  getTimetableIsLoading,
} from '~/store/reducers/timetable'
import { getMultipleUsers } from '~/store/reducers/users'
import type { TimetableEvent, WidgetColumn } from '~/types'
import { addOriginalBusinessId } from '~/utils'
import { getIsOutsideCancellationWindow } from '~/utils/appointmentCancellationUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'
import { useAppointmentStateId } from '~/utils/useEventType'
import useIsDymoConnectEnabled from '~/utils/useIsDymoEnabled'

import AppointmentTypeCell from '../AppointmentTypeCell'
import ClientAndPatientCell from '../ClientAndPatientCell'
import DateGroupingCell from '../DateGroupingCell'
import DateRangeCell from '../DateRangeCell'
import LandingWidget, { type LandingWidgetProps } from '../LandingWidget'
import NoRecords from '../NoRecords'
import StatusSelectActionPopper from '../StatusSelectActionPopper'
import TableWidget from '../TableWidget'
import useEnrichGroupedItemsByDate from '../useEnrichGroupedItemsByDate'
import { getVisibleWidgetColumns } from '../widgetUtils'
import AppointmentFullStatusCell from './AppointmentFullStatusCell'
import AppointmentSmallStatusCell from './AppointmentSmallStatusCell'

const columns: WidgetColumn<TimetableEvent>[] = [
  {
    id: 'dateGrouping',
    prop: (item) => <DateGroupingCell title={item?.dateTitle} />,
    widthMap: {
      [WidgetWidthType.FULL_WIDTH]: 1.5,
      [WidgetWidthType.TWO_THIRDS_WIDTH]: 1.5,
      [WidgetWidthType.HALF_WIDTH]: 1.5,
      [WidgetWidthType.THIRD_WIDTH]: 2,
    },
    label: i18n.t('Common:DATE_TIME'),
  },
  {
    id: 'dateRange',
    component: DateRangeCell,
    widthMap: {
      [WidgetWidthType.FULL_WIDTH]: 2,
      [WidgetWidthType.TWO_THIRDS_WIDTH]: 2,
      [WidgetWidthType.HALF_WIDTH]: 2,
      [WidgetWidthType.THIRD_WIDTH]: 2,
    },
    label: i18n.t('Common:TIME'),
  },
  {
    id: 'clientAndPatient',
    component: ClientAndPatientCell,
    widthMap: {
      [WidgetWidthType.FULL_WIDTH]: 2,
      [WidgetWidthType.TWO_THIRDS_WIDTH]: 2,
      [WidgetWidthType.HALF_WIDTH]: 2,
      [WidgetWidthType.THIRD_WIDTH]: 2.5,
    },
    label: i18n.t('Common:CLIENT_AND_PATIENT'),
  },
  {
    id: 'notes',
    prop: 'notes',
    width: 1.5,
    widthTypes: [WidgetWidthType.FULL_WIDTH],
    label: i18n.t('Common:NOTES'),
  },
  {
    id: 'appointmentType',
    component: AppointmentTypeCell,
    width: 1.5,
    widthTypes: [WidgetWidthType.FULL_WIDTH],
    label: i18n.t('Common:APPOINTMENT_TYPE'),
  },
  {
    id: 'spaceName',
    prop: (item) => item.space?.name,
    width: 1.5,
    widthTypes: [WidgetWidthType.FULL_WIDTH],
    label: i18n.t('Common:SPACE_ONE'),
  },
  {
    id: 'appointmentFullStatus',
    component: AppointmentFullStatusCell,
    width: 1.5,
    widthTypes: [WidgetWidthType.FULL_WIDTH],
    label: i18n.t('Common:STATUS'),
  },
  {
    id: 'appointmentSmallStatus',
    component: AppointmentSmallStatusCell,
    widthMap: {
      [WidgetWidthType.FULL_WIDTH]: 0.5,
      [WidgetWidthType.TWO_THIRDS_WIDTH]: 0.5,
      [WidgetWidthType.HALF_WIDTH]: 0.5,
      [WidgetWidthType.THIRD_WIDTH]: 1,
    },
    label: i18n.t('Common:STATUS'),
  },
]

interface UpcomingWidgetProps extends LandingWidgetProps<TimetableEvent> {
  onExpand: () => void
  rowsCount?: number
  widthType?: WidgetWidthType
}

const UpcomingWidget = ({
  widthType = WidgetWidthType.FULL_WIDTH,
  rowsCount = 10,
  onExpand,
  ...rest
}: UpcomingWidgetProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Landing'])

  const AppointmentsWidgetAssignedMode = useSelector(
    getAppointmentsWidgetAssignedMode,
  )
  const appointmentIds = useSelector(
    getWidgetData(LandingType.LANDING_DASHBOARD, LandingWidgetName.UPCOMING),
  )
  const totalCount = useSelector(
    getWidgetDataTotalCount(
      LandingType.LANDING_DASHBOARD,
      LandingWidgetName.UPCOMING,
    ),
  )
  const widgetDataIsLoading = useSelector(
    getWidgetDataIsLoading(
      LandingType.LANDING_DASHBOARD,
      LandingWidgetName.UPCOMING,
    ),
  )
  const soapIsLoading = useSelector(getSOAPisLoading)
  const appointmentsIsLoading = useSelector(getTimetableIsLoading)
  const appointments = useSelector(getMultipleTimetableEvents(appointmentIds))
  const clients = useSelector(
    getMultipleUsers(
      appointments
        .map((item) => item.clientId)
        .filter((item) => Boolean(item)) as string[],
    ),
  )
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const isDymoConnectEnabled = useIsDymoConnectEnabled()

  const visibleAppointments = appointments.slice(0, rowsCount)
  const isLoading =
    widgetDataIsLoading || soapIsLoading || appointmentsIsLoading

  const appointmentsList = useEnrichGroupedItemsByDate({
    dateProp: 'scheduledStartDatetime',
    titleProp: 'dateTitle',
    items: visibleAppointments,
  })

  const [openInvoiceDialog] = useDialog(DialogNames.INVOICE)
  const [openPrintInvoiceDialog] = useDialog(DialogNames.PRINT_INVOICE)
  const [openPrintCageLabelDialog] = useDialog(DialogNames.PRINT_CAGE_LABEL)
  const [openPrintFolderLabelDialog] = useDialog(DialogNames.PRINT_FOLDER_LABEL)
  const [openNewConversationDialog] = useDialog(
    DialogNames.NEW_CONVERSATION_WITH_STEPS,
  )
  const [openAppointmentDialog] = useDialog(DialogNames.EVENT)

  const isAppointmentCancellationReasonEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_CANCELLATION_REASON),
  )
  const isNoShowPenaltyRefundEnabled = useSelector(
    getFeatureToggle(FeatureToggle.NO_SHOW_CANCELLATION_PENALTY_REFUND),
  )
  const isCurrentBusinessIsAppointmentCancellationReasonEnabled = useSelector(
    getCurrentBusinessIsAppointmentCancellationReasonEnabled,
  )
  const cancelledStateId = useAppointmentStateId(AppointmentState.CANCELLED)
  const noShowStateId = useAppointmentStateId(AppointmentState.NO_SHOW)

  const [openAppointmentCancellationReasonDialog] = useDialog(
    DialogNames.APPOINTMENT_CANCELLATION_REASON,
  )
  const [openConfirmAppointmentNoShowAlert] = useConfirmAppointmentNoShowAlert()

  const isNoShowConsentAppointmentType = useGetIsNoShowConsentAppointmentType()

  const isNoShowCancellationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.NO_SHOW_CANCELLATION_PENALTY),
  )

  const isDepositPayByLinkEnabled = useSelector(
    getFeatureToggle(FeatureToggle.DEPOSIT_PAY_BY_LINK),
  )

  const isNoShowCancellationPenaltyCardOnFileAlertEnabled = useSelector(
    getFeatureToggle(
      FeatureToggle.NO_SHOW_CANCELLATION_PENALTY_CARD_ON_FILE_ALERT,
    ),
  )

  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)

  const showNoShowAlert = (item: TimetableEvent) =>
    isNoShowPenaltyRefundEnabled &&
    isOmnichannel &&
    isNoShowConsentAppointmentType({
      appointmentTypeId: item?.type?.id,
      businessAppointmentTypeId: item?.businessAppointmentType?.id,
    })
  const showNoShowCancellationPenaltyAlert = (item: TimetableEvent) =>
    isNoShowCancellationEnabled &&
    isNoShowCancellationPenaltyCardOnFileAlertEnabled &&
    isOmnichannel &&
    isNoShowConsentAppointmentType({
      appointmentTypeId: item?.type?.id,
      businessAppointmentTypeId: item?.businessAppointmentType?.id,
    }) &&
    getIsOutsideCancellationWindow(
      item?.scheduledStartDatetime,
      item?.scheduledEndDatetime,
    )

  const showHighValueDepositAlerts = (item: TimetableEvent) =>
    isDepositPayByLinkEnabled &&
    isOmnichannel &&
    isHighValueAppointment(item?.type?.name || '')

  const openInvoice = useOpenInvoice(null, openInvoiceDialog)

  const goToSoap = (item: TimetableEvent) => {
    navigate(
      addOriginalBusinessId(
        `/soap/${item.soapId}`,
        isPatientSharingEnabled ? item.businessId : null,
      ),
    )
  }

  const handleNavigateToItemDetails = goToSoap

  const handleGoToSoap = goToSoap

  const handleCreateSoap = (item?: TimetableEvent) => {
    if (item) {
      dispatch(
        createSOAP(item.id, isPatientSharingEnabled ? item.businessId : null),
      )
    }
  }

  const handleOpenInvoice = (item: TimetableEvent) => {
    openInvoice({
      clientId: item.clientId,
      patientId: item.patientId,
      invoiceId: item.invoiceId,
      soapId: item.soapId,
    })
  }

  const handlePrintInvoice = (item: TimetableEvent) =>
    openPrintInvoiceDialog({
      invoiceIds: item.invoiceId ? [item.invoiceId] : [],
    })

  const handleGoToPatient = (item?: TimetableEvent) => {
    if (item) {
      navigate(`/client/${item.clientId}/patient/${item.patientId}`)
    }
  }

  const handlePrintCageCard = (item?: TimetableEvent) => {
    openPrintCageLabelDialog({
      clientId: item?.clientId,
      patientId: item?.parentId,
    })
  }
  const handlePrintFolderLabel = (item?: TimetableEvent) =>
    openPrintFolderLabelDialog({
      clientId: item?.clientId,
      patientId: item?.parentId,
    })

  const navigateToConversation = (id?: string) => {
    if (id) {
      navigate(`${BaseRoute.COMMUNICATIONS}/${id}`)
    }
  }

  const handleCreateNewConversation = (item?: TimetableEvent) => {
    openNewConversationDialog({
      clientId: item?.clientId,
      patientId: item?.patientId,
      appointmentId: item?.id,
    })
  }

  const handleOpenConversation = ({ conversationId }: TimetableEvent) =>
    navigateToConversation(conversationId)

  const refreshAppointmentWidgets = () => {
    dispatch(
      fetchWidgetsData(
        [LandingWidgetName.ADMITTED, LandingWidgetName.UPCOMING],
        {
          landingType: LandingType.LANDING_DASHBOARD,
          quiet: false,
        },
      ),
    )
  }

  const refreshAfterSave = useCloseAfterCreation(
    refreshAppointmentWidgets,
    getTimetableIsLoading,
  )

  const handleStatusChange = (value: Constant, item?: TimetableEvent) => {
    if (item) {
      if (
        isAppointmentCancellationReasonEnabled &&
        isCurrentBusinessIsAppointmentCancellationReasonEnabled &&
        value.id === cancelledStateId
      ) {
        openAppointmentCancellationReasonDialog({
          onProceed: (
            cancellationReasonId: string,
            cancellationReason: string,
            internalNotes: string,
          ) => {
            dispatch(
              patchAppointment({
                id: item.id,
                state: value.id,
                cancellationReasonId,
                cancellationReason,
                internalNotes,
              }),
            )
            refreshAfterSave()
          },
          showNoShowCancellationPenaltyAlert:
            showNoShowCancellationPenaltyAlert(item),
          showDepositAlert: showHighValueDepositAlerts(item),
          isAppointmentWithin24Hours: getIsOutsideCancellationWindow(
            item.scheduledStartDatetime,
            item.scheduledEndDatetime,
          ),
          isNoShowConsentAppointmentType: isNoShowConsentAppointmentType({
            appointmentTypeId: item?.type?.id,
            businessAppointmentTypeId: item?.businessAppointmentType?.id,
          }),
        })
      } else if (
        showHighValueDepositAlerts(item) &&
        value.id === noShowStateId
      ) {
        openConfirmAppointmentNoShowAlert({
          onConfirm: (proceed: boolean) => {
            if (proceed) {
              dispatch(patchAppointment({ id: item.id, state: value.id }))
              refreshAfterSave()
            }
          },
          isHighValueAppointment: true,
        })
      } else if (showNoShowAlert(item) && value.id === noShowStateId) {
        openConfirmAppointmentNoShowAlert({
          onConfirm: (proceed: boolean) => {
            if (proceed) {
              dispatch(patchAppointment({ id: item.id, state: value.id }))
              refreshAfterSave()
            }
          },
          isHighValueAppointment: false,
          client: clients.find((user) => user.id === item.clientId),
        })
      } else {
        dispatch(patchAppointment({ id: item.id, state: value.id }))
        refreshAfterSave()
      }
    }
  }

  const handleCreateAppointment = () => {
    openAppointmentDialog()
  }

  const handleNavigateToScheduler = () => navigate('/scheduler')

  const getActions = (item?: TimetableEvent) =>
    [
      {
        id: 'soapAndAppointment',
        isGroup: true,
        items: [
          item?.soapId
            ? {
                id: 'goToSoap',
                label: t('Common:GO_TO_SOAP'),
                Icon: ArrowForwardIcon,
                onClick: () => handleGoToSoap(item),
              }
            : {
                id: 'createSoap',
                label: t('Common:CREATE_SOAP'),
                Icon: AddIcon,
                onClick: () => handleCreateSoap(item),
              },
          {
            id: 'appointmentStatus',
            label: t('Common:APPOINTMENT_STATUS'),
            Icon: WhiteboardIcon,
            subItem: StatusSelectActionPopper,
            onClick: (value: Constant) => handleStatusChange(value, item),
          },
        ],
      },
      item?.invoiceId && {
        id: 'invoice',
        title: t('Common:INVOICE'),
        isGroup: true,
        items: [
          {
            id: 'openInvoice',
            label: t('Common:OPEN_INVOICE'),
            Icon: EstimatesIcon,
            onClick: () => handleOpenInvoice(item),
          },
          {
            id: 'printInvoice',
            label: t('Common:PRINT_INVOICE'),
            Icon: PrintIcon,
            onClick: () => handlePrintInvoice(item),
          },
        ],
      },
      {
        id: 'patient',
        title: t('Common:PATIENT'),
        isGroup: true,
        items: [
          {
            id: 'goToPatientDetailsInvoice',
            label: t('Common:GO_TO_PATIENT_DETAILS'),
            Icon: ClientAndPatientIcon,
            onClick: () => handleGoToPatient(item),
          },
          {
            id: 'printCageCardLabel',
            label: t('Common:PRINT_CAGE_CARD_LABEL'),
            Icon: PrintIcon,
            onClick: () => handlePrintCageCard(item),
          },
          isDymoConnectEnabled && {
            id: 'printFolderLabel',
            label: t('Common:PRINT_FOLDER_LABEL'),
            Icon: PrintIcon,
            onClick: () => handlePrintFolderLabel(item),
          },
        ],
      },
      {
        id: 'client',
        title: t('Common:CLIENT'),
        isGroup: true,
        items: [
          item?.conversation?.id
            ? {
                id: 'openConversation',
                label: t('Common:OPEN_CONVERSATION'),
                Icon: SmsIcon,
                onClick: () => handleOpenConversation(item),
              }
            : {
                id: 'newConversation',
                label: t('Common:NEW_CLIENT_CONVERSATION'),
                Icon: CommunicationsIcon,
                onClick: () => handleCreateNewConversation(item),
              },
        ],
      },
    ] as PopperAction[]

  const visibleColumns = getVisibleWidgetColumns(columns, widthType)

  return (
    <LandingWidget
      showHeaderTitles
      assignedModes={AppointmentsWidgetAssignedMode}
      canNavigateToItemDetails={(item) => Boolean(item.soapId)}
      columns={visibleColumns}
      component={TableWidget}
      data={appointmentsList}
      getActions={getActions}
      isLoading={isLoading}
      navigateToDetailsTooltip={t('Common:GO_TO_SCHEDULER')}
      navigateToItemDetailsTooltip={t('Common:GO_TO_SOAP')}
      noRecords={
        <NoRecords
          action={t('Common:ADD_APPOINTMENT')}
          text={t('Landing:UPCOMING_WIDGET.NO_UPCOMING_APPOINTMENTS')}
          onClick={handleCreateAppointment}
        />
      }
      rowsCount={rowsCount}
      totalCount={totalCount}
      onExpand={onExpand}
      onNavigateToDetails={handleNavigateToScheduler}
      onNavigateToItemDetails={handleNavigateToItemDetails}
      {...rest}
    />
  )
}

export default UpcomingWidget
